import React, { useState } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { InputFieldWrapper, DropDownLabel } from './styles';

const DropDown = (props) => {
  const {
    label,
    id,
    items = [],
    selectedValue = '',
    isSearchable = false,
    isFontSelection = false,
    noHitsMessage = i18n.__('app_no_matches'),
    placeholder,
    onChange,
    theme,
    disabled,
    colors,
    maxWidth,
    lightMode = false,
    ...rest
  } = props;

  const [value, setValue] = useState(selectedValue);

  const dropDownLabel = label ? <DropDownLabel htmlFor={id}>{label}</DropDownLabel> : null;

  const selectedOption = items.find((item) => item.value.toString().toUpperCase() === value.toString().toUpperCase());

  const onItemChange = (item) => {
    setValue(item.value);
    if (onChange) {
      onChange({ target: { ...item, id } });
    }
  };

  const getColors = (state) => {
    const themeColors = state.selectProps.theme.colors;
    return {
      front: colors ? colors.front : lightMode ? themeColors.standardBlackBackground : themeColors.white,
      back: colors ? colors.back : lightMode ? themeColors.white : themeColors.standardBlackBackground,
      selected: colors ? colors.back : lightMode ? themeColors.black_1 : themeColors.white_1,
      border: themeColors.iconButtonBorder,
      borderHover: themeColors.iconButtonBorderHover,
      borderBottom: themeColors.grey,
      placeholder: lightMode ? themeColors.standardBlackBackground : themeColors.white_7,
      drowdownArrow: lightMode ? themeColors.standardBlackBackground : themeColors.white,
    };
  };

  const dropdownStyle = {
    container: (provided) => ({
      ...provided,
      width: 'auto',
      maxWidth,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor:
        state.isSelected || state.isFocused ? `${getColors(state).selected}` : `${getColors(state).back}`,
      color: `${getColors(state).front}`,
      padding: 10,
      cursor: 'pointer',
      border: `1px solid ${getColors(state).border}`,
      borderBottom: `1px solid ${getColors(state).borderBottom}`,
      fontFamily: isFontSelection ? state.data.value : 'Roboto',
      display: 'flex',
      minHeight: '40px',
    }),
    menuList: (provided, state) => {
      return {
        ...provided,
        padding: 0,
        '& :first-child': {
          borderRadius: '5px 5px 0 0',
        },
        '& :last-child': {
          borderRadius: '0 0 5px 5px',
        },
      };
    },
    control: (provided, state) => ({
      ...provided,
      cursor: 'pointer',
      backgroundColor: `${getColors(state).back}`,
      color: `${getColors(state).front}`,
      borderRadius: 5,
      transition: `border 0.3s ease`,
      border: `2px solid ${state.isFocused ? getColors(state).borderHover : getColors(state).border}`,
      ':hover': {
        borderColor: `${getColors(state).borderHover}`,
        boxShadow: 'none',
      },
      maxWidth,
      opacity: state.selectProps.isDisabled ? 0.6 : 1,
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: `${getColors(state).front}`,
      display: 'flex',
    }),
    input: (provided) => ({
      ...provided,
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      cursor: state.selectProps.isSearchable ? 'text' : 'pointer',
    }),
    menu: (provided, state) => ({
      ...provided,
      backgroundColor: `${getColors(state).back}`,
      padding: 0,
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: `${getColors(state).drowdownArrow}`,
      ':hover': {
        borderColor: `${getColors(state).drowdownArrow}`,
      },
    }),
    placeholder: (provided, state) => ({
      ...provided,
      color: `${getColors(state).placeholder}`,
      fontStyle: 'italic',
    }),
    noOptionsMessage: (provided, state) => ({
      ...provided,
      color: `${getColors(state).front}`,
    }),
  };

  const placeholderText = placeholder || (isSearchable ? i18n.__('app_select_or_search') : i18n.__('app_select'));

  return (
    <InputFieldWrapper maxWidth={maxWidth} {...rest}>
      {dropDownLabel}
      <Select
        inputId={id}
        name={id}
        value={selectedOption}
        isSearchable={isSearchable}
        options={items}
        theme={theme}
        noOptionsMessage={() => noHitsMessage}
        onChange={onItemChange}
        onKeyPress={onItemChange}
        styles={dropdownStyle}
        placeholder={placeholderText}
        components={{
          IndicatorSeparator: () => null,
        }}
        isDisabled={disabled}
        {...rest}
      />
    </InputFieldWrapper>
  );
};

DropDown.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string.isRequired,
  items: PropTypes.array,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  isSearchable: PropTypes.bool,
  isFontSelection: PropTypes.bool,
  disabled: PropTypes.bool,
  noHitsMessage: PropTypes.string,
  onChange: PropTypes.func,
  colors: PropTypes.object,
  maxWidth: PropTypes.number,
  placeholder: PropTypes.string,
  lightMode: PropTypes.bool,
  rest: PropTypes.object,
};

export default withTheme(DropDown);
